import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { callGetConfigsViaApiService, GeocodingConfig, GeocodingConfigsResponse } from '~src/geocoding/requests/GeocodingConfigRequests';
import { callGetColumnsViaApiService, GetDatasourceColumnsResponse } from '~src/geocoding/requests/GetDatasourceColumns';
import { GeocodingResponseHelper } from '~src/geocoding/utils/GeocodingResponseHelper';

import { CreateCustomDialogFn, ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { LauchGeocodingConfigDialog } from '../configDialog/GeocodingConfigDialog';
import { GeocodingRecordDataGrid } from './GeocodingRecordDataGrid';

export interface GeocodingRecordDialogProps {
  datasourceName: string;
  datasourceLuid: string;
  close: () => void;
}

/**
 * Options used for ReactModalService
 */
export function LaunchGeocodingRecordDialog(props: Omit<GeocodingRecordDialogProps, 'close'>): CreateCustomDialogFn {
  return (closeAndDismiss) => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: Messages.geocodingDialogTitle({DS_NAME: props.datasourceName}),
      dialogWidth: 650,
      shouldNotCloseOnGlassClick: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showCloseButton: true,
      testId: 'geocoding-record-dialog'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <GeocodingRecordDialog
          datasourceName={props.datasourceName}
          datasourceLuid={props.datasourceLuid}
          close={closeAndDismiss.close}
        />
      </DialogWidget>
    );
  };
}

interface GeocodingRecordDialogState {
  configRecords: GeocodingConfig[];
}

export class GeocodingRecordDialog extends React.Component<GeocodingRecordDialogProps, GeocodingRecordDialogState> {
  public constructor(props: GeocodingRecordDialogProps) {
    super(props);
    this.state = {
      configRecords: [],
    };
    this.getGeocodingConfigsForDatasource(this.props.datasourceLuid);
  }

    private renderFooterButton(): JSX.Element {
      return (
        <div className={'geocodingFooterButtonContainer'}>
          <ButtonWidget buttonType={ButtonType.Primary} onClick={this.props.close}>
            {Messages.geocodingDialogOkButton()}
          </ButtonWidget>
        </div>
      );
    }

    public getGeocodingConfigsForDatasource(datasourceLuid: string): Promise<void> {
      return callGetConfigsViaApiService({datasourceLuid})
      .then(
        (result: GeocodingConfigsResponse) => {
          const configs = GeocodingResponseHelper.processGetGeocodingConfigsForDatasourceResponse(result);
          this.setState({ configRecords: configs });
          return Promise.resolve();
          },
        (reason: GeocodingConfigsResponse) => {
          return Promise.reject(reason);
        }
      );
    }

    private handleUpdateRecord = () => {
      this.getGeocodingConfigsForDatasource(this.props.datasourceLuid);
    };

    private handleNewEditConfig = (geocodingConfig?: GeocodingConfig) => {
      callGetColumnsViaApiService({datasourceLuid: this.props.datasourceLuid})
      .then(
        (result: GetDatasourceColumnsResponse) => {
          const { columns, colNameToCaptionMap } = GeocodingResponseHelper.processGetDatasourceColumnsResponse(result);
          ReactModalService.openCustom(LauchGeocodingConfigDialog({
            datasourceName: this.props.datasourceName,
            datasourceLuid: this.props.datasourceLuid,
            geocodingConfig,
            columns,
            colNameToCaptionMap,
            updateRecord: this.handleUpdateRecord}));
          return Promise.resolve();
        },
        (reason: GetDatasourceColumnsResponse) => {
          // TODO: show or handle error when getColumns fails
          return Promise.reject(reason);
        }
      );
    };

    private buildNewGeocodeButton(): React.ReactNode {
      return (
        <ButtonWidget className='newGeocodeButton' testId='geoRecordDialogNew' buttonType={ButtonType.Outline} onClick={() => this.handleNewEditConfig()}>
          {Messages.geocodingRecordDialogNewButton()}
        </ButtonWidget>
      );
    }

    private buildRecordDataGridArea(): React.ReactNode {
      if (this.state.configRecords.length === 0) {
        return <div>{Messages.geocodingRecordEmptyDatagrid()}</div>;
      }
      return (
        <>
          <div>{Messages.geocodingRecordDialogContentInfo()}</div>
          <GeocodingRecordDataGrid configRecords={this.state.configRecords} handleEditConfig={this.handleNewEditConfig}/>
        </>
      );
    }

    public render(): JSX.Element {
      return (
        <div>
          <div className='geocodingLargeText'>{Messages.geocodingRecordDialogContentHeader()}</div>
          {this.buildRecordDataGridArea()}
          {this.buildNewGeocodeButton()}
          {this.renderFooterButton()}
        </div>
      );
    }
  }
